import React, { FC, Fragment, useCallback, useState } from "react";
import { StockNumericType } from "data/enums";
import { stockDetailPath } from "data/stock_types";
import CommonLayout from "./CommonLayout";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectBookings } from "application/features/bookings/bookingsSlice";
import { useStartBookingMutation } from "application/features/bookings/bookingApiSlice";
import { selectEvents } from "application/features/calendar/calendarSlice";
import moment from "moment";
import Badge from "shared/Badge/Badge";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Heading from "components/Heading/Heading";
import "./carousel.css";
import SummaryDrawer from "./SummaryDrawer";
import MultiStockCard from "components/Quasi/MultiStockCard/MultiStockCard";
import type { BookingDataType } from "data/interfaces";
import { isStay, isExperience, isHolidayPackage } from "data/interfaces";
import { TrashIcon } from "@heroicons/react/24/outline";
import { setBookings } from "application/features/bookings/bookingsSlice";
import { formatPrice } from "shared/AppFunctions/formatPrice";
import HolidayPackagePanelGuard from "containers/MyTripPage/HolidayPackagePanelGuard";
import OnlyInPackageStayGuard from "containers/MyTripPage/OnlyInPackageStayGuard";
import { useCartRequestHandlers } from "application/hooks/useCartRequestHandlers";
import { useTranslation } from "react-i18next";

export interface PageAddListing1Props {}

const SummaryStep: FC<PageAddListing1Props> = () => {
  const { t } = useTranslation("translation");
  const bookings = useSelector(selectBookings);
  const events = useSelector(selectEvents);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [selectedExperiences, setSelectedExperiences] = useState<any>([]);
  const [selectedProperty, setSelectedProperty] = useState<any>({});

  // Per-holiday-package problematic state, populated by the panel guards.
  const [problematicIds, setProblematicIds] = useState<Set<string | number>>(
    () => new Set(),
  );
  const [onlyInPackageStayIds, setOnlyInPackageStayIds] = useState<Set<string | number>>(
    () => new Set(),
  );

  const handleProblematicChange = useCallback(
    (id: string | number, isProblematic: boolean) => {
      setProblematicIds((prev) => {
        const next = new Set(prev);
        if (isProblematic) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    [],
  );

  const handleOnlyInPackageStayChange = useCallback(
    (id: string | number, isProblematic: boolean) => {
      setOnlyInPackageStayIds((prev) => {
        const next = new Set(prev);
        if (isProblematic) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    [],
  );

  const {
    handleRemoveFromTrip,
    handleCartSendRequest,
    handleChangeDates,
    isSendingRequest,
  } = useCartRequestHandlers();

  const problematic = bookings.filter((b) => problematicIds.has(b.id));
  const hasProblematic = problematic.length > 0;

  const prevUrl = `/client/booking/calendar`;

  const handleRemoveBooking = (booking_id: string | number) => {
    const newBookings = bookings.filter(
      (booking: BookingDataType) => booking?.id !== booking_id,
    );
    dispatch(setBookings(newBookings));
  };

  const returnTotalPrice = (bookings: any, events: any): string => {
    let total = 0;
    bookings?.forEach((item: any) => {
      total += Number(item.price || 0) + Number(item.additional_fees || 0);
    });
    events?.forEach((item: any) => {
      total += Number(item.price || 0);
    });
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(total);
  };

  const handleOpenDrawer = (item: any) => {
    setLoading(true);
    setSelectedProperty(item.property);
    const experiences = events?.filter((event: any) => {
      const eventStartDate = moment(event?.start).format("YYYY-MM-DD");
      if (
        moment(eventStartDate).isBetween(
          moment(item?.startDate),
          moment(item?.endDate),
          undefined,
          "[]",
        )
      ) {
        return event;
      }
    });
    setSelectedExperiences(experiences);
    setIsOpen(true);
    setLoading(false);
  };

  return (
    <CommonLayout
      index="2"
      backtHref={prevUrl}
      canGoNext={!hasProblematic && onlyInPackageStayIds.size === 0}
      nextHref="/client/booking/payment"
    >
      <div className=" flex flex-col lg:px-[10rem] md:py-[2rem] px-[2rem] pt-[1rem] pb-[5rem]">
        <Heading
          desc={t("translation:detailPages.common.bookingSummaryDescription")}
        >
          {t("translation:detailPages.common.bookingSummaryTitle")}
        </Heading>
        {hasProblematic && (
          <div
            role="alert"
            className="mb-4 p-4 rounded-lg border border-red-300 bg-red-50 text-red-800"
          >
            {t("translation:detailPages.common.cartValidationBanner", {
              count: problematic.length,
            })}
          </div>
        )}
        {onlyInPackageStayIds.size > 0 && (
          <div
            role="alert"
            className="mb-4 p-4 rounded-lg border border-red-300 bg-red-50 text-red-800"
          >
            Alcune strutture sono prenotabili solo all'interno di un pacchetto. Rimuovile per continuare.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {bookings?.map((booking: BookingDataType) => {
            // Extract data based on booking type
            let cover: string = "";
            let images: string[] = [];
            let uuid: string | number = "";
            let href: string = "";
            let address: string = "";
            let annotation: string = "";
            let totalEvents = 0;
            const price =
              Number(booking.price) + Number(booking.additional_fees || 0);

            // Count events for this booking (events are now shown as separate cards)
            events?.forEach((event: any) => {
              const eventStartDate = moment(event?.start).format("YYYY-MM-DD");
              if (
                moment(eventStartDate).isBetween(
                  moment(booking?.startDate),
                  moment(booking?.endDate),
                  undefined,
                  "[]",
                )
              ) {
                totalEvents++;
              }
            });

            if (isStay(booking.item)) {
              uuid = booking.item.property_uuid;
              cover = booking.item.cover || booking.item.place_cover || "";
              const galleryImages = booking.item.gallery_images || [];
              images =
                galleryImages.length > 0 ? galleryImages : cover ? [cover] : [];
              href = stockDetailPath(StockNumericType.STAY, uuid);
              annotation = `${booking.item.property_type}`;
              address = booking.item.address;
            } else if (isExperience(booking.item)) {
              uuid = booking.item.experience_uuid;
              cover = booking.item.cover || booking.item.featuredImage || "";
              const galleryImages = booking.item.gallery_images || [];
              images =
                galleryImages.length > 0 ? galleryImages : cover ? [cover] : [];
              href = stockDetailPath(StockNumericType.EXPERIENCE, uuid);
              annotation = `${booking.item.type}`;
              address = booking.item.city;
            } else if (isHolidayPackage(booking.item)) {
              uuid = booking.item.package_uuid;
              cover = booking.item.cover_image || "";
              const galleryImages = booking.item.gallery_images || [];
              images =
                galleryImages.length > 0 ? galleryImages : cover ? [cover] : [];
              address = booking.item.location || "";
              annotation = booking.item.category
                ? `${booking.item.category}`
                : t("translation:detailPages.common.holidayPackage");
              href = stockDetailPath(StockNumericType.HOLIDAY_PACKAGE, uuid);
            } else {
              return null;
            }

            // Holiday packages wrap their card in a div so the panel
            // (rendered conditionally by HolidayPackagePanelGuard) sits
            // visually below the card. Stays and experiences render as
            // direct grid items.
            if (isHolidayPackage(booking.item)) {
              return (
                <div key={booking.id} className="flex flex-col">
                  <MultiStockCard
                    id={uuid}
                    cover={cover}
                    annotation={annotation}
                    images={images}
                    title={booking.item.name}
                    address={address}
                    mood={booking.item.mood}
                    price={price}
                    max_guests={booking.guests}
                    href={href}
                    orientation="vertical"
                    loading={false}
                  >
                    <div className="flex flex-col gap-3 w-full">
                      <div className="flex items-center justify-between text-sm text-neutral-600 dark:text-neutral-400">
                        <span>
                          {moment(booking.startDate).format("DD/MM/YYYY")} -{" "}
                          {moment(booking.endDate).format("DD/MM/YYYY")}
                        </span>
                        <span>
                          {totalEvents}{" "}
                          {t("translation:detailPages.common.experiences")}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-semibold">
                          {formatPrice(
                            price,
                            true,
                            window.navigator.language,
                            "EUR",
                          )}
                        </span>
                        <div className="flex gap-2">
                          <ButtonPrimary
                            loading={loading}
                            onClick={() => handleOpenDrawer(booking)}
                            className="!py-2 !px-4 text-sm"
                          >
                            {t("translation:detailPages.common.details")}
                          </ButtonPrimary>
                        </div>
                      </div>
                    </div>
                  </MultiStockCard>
                  <HolidayPackagePanelGuard
                    booking={booking}
                    onSendRequest={handleCartSendRequest}
                    onRemove={handleRemoveFromTrip}
                    onChangeDates={handleChangeDates}
                    isSendingRequest={isSendingRequest}
                    onProblematicChange={handleProblematicChange}
                  />
                </div>
              );
            }

            return (
              <Fragment key={booking.id}>
                <MultiStockCard
                  id={uuid}
                  cover={cover}
                  annotation={annotation}
                  images={images}
                  title={booking.item.name}
                  address={address}
                  mood={booking.item.mood}
                  price={price}
                  max_guests={booking.guests}
                  href={href}
                  orientation="vertical"
                  loading={false}
                >
                <div className="flex flex-col gap-3 w-full">
                  <div className="flex items-center justify-between text-sm text-neutral-600 dark:text-neutral-400">
                    <span>
                      {moment(booking.startDate).format("DD/MM/YYYY")} -{" "}
                      {moment(booking.endDate).format("DD/MM/YYYY")}
                    </span>
                    <span>
                      {totalEvents}{" "}
                      {t("translation:detailPages.common.experiences")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold">
                      {formatPrice(
                        price,
                        true,
                        window.navigator.language,
                        "EUR",
                      )}
                    </span>
                    <div className="flex gap-2">
                      <ButtonPrimary
                        loading={loading}
                        onClick={() => handleOpenDrawer(booking)}
                        className="!py-2 !px-4 text-sm"
                      >
                        {t("translation:detailPages.common.details")}
                      </ButtonPrimary>
                    </div>
                  </div>
                </div>
              </MultiStockCard>
              <OnlyInPackageStayGuard
                booking={booking}
                onRemove={handleRemoveFromTrip}
                onProblematicChange={handleOnlyInPackageStayChange}
              />
              </Fragment>
            );
          })}
          {// Render individual experience events as cards
          events?.map((event: any, index: number) => {
            const eventCover = event.cover || event.featuredImage || "";
            const eventUuid = event.experience_uuid;
            const eventHref = stockDetailPath(StockNumericType.EXPERIENCE, eventUuid);
            const eventAddress = event.city || "";
            const eventAnnotation =
              event.type || t("translation:detailPages.common.experience");
            const eventImages =
              event.gallery_images?.length > 0
                ? event.gallery_images
                : eventCover
                  ? [eventCover]
                  : [];

            return (
              <MultiStockCard
                key={`event-${index}-${event.experience_uuid}`}
                id={eventUuid}
                cover={eventCover}
                annotation={eventAnnotation}
                images={eventImages}
                title={event.name}
                address={eventAddress}
                mood={event.mood}
                href={eventHref}
                orientation="vertical"
                loading={false}
              >
                <div className="flex flex-col gap-3 w-full">
                  <div className="flex items-center justify-between text-sm text-neutral-600 dark:text-neutral-400">
                    <span>
                      {moment(event.start).format("DD/MM/YYYY HH:mm")} -{" "}
                      {moment(event.end).format("HH:mm")}
                    </span>
                    <Badge
                      color="green"
                      name={t("translation:detailPages.common.experience")}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold">
                      {formatPrice(
                        Number(event.price),
                        true,
                        window.navigator.language,
                        "EUR",
                      )}
                    </span>
                  </div>
                </div>
              </MultiStockCard>
            );
          })}
        </div>
      </div>
      <SummaryDrawer
        isOpen={isOpen}
        experiences={selectedExperiences}
        property={selectedProperty}
        closeModal={() => setIsOpen(false)}
      />
    </CommonLayout>
  );
};

export default SummaryStep;
